/**
 * Reschedule requests tab (tracker item 45, phase 4). Each request is a card with the customer, the job's
 * property, the current and the requested date and time, and for an OPEN one Approve and Decline:
 *  - Approve applies the requested date and time through the normal job update; the office may change
 *    the date or time first. When the dispatch checks find a conflict (overlap, working hours, time off)
 *    the server answers 409 RESCHEDULE_CONFLICTS: the conflicts are listed and the office must tick a
 *    confirmation before "Approve anyway" sends overrideConflicts.
 *  - Decline needs a message, which the customer sees.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IRequestsApi } from '../../api';
import { RescheduleRequest, RescheduleRequestStatus, ScheduleConflict } from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { RequestPager } from '../../components/RequestPager';
import { Toast } from '../../components/Toast';
import {
  ApprovalDraft, RESCHEDULE_FILTERS, RESCHEDULE_OFFICE_LABELS, addressLine, approvalBody, approvalDraft, approvalProblem, conflictsOf,
  dateTimeLabel, isStaleRequestError, requestActionError, requiredTextProblem, statusBadgeClass, whenLabel,
} from './requestsUi';
import { INBOX_PAGE_SIZE, useInboxList } from './useInboxList';

interface CardProps {
  request: RescheduleRequest;
  api: IRequestsApi;
  /** Reload the list; `message` is shown as a success toast */
  onChanged: (message?: string) => void;
}

function RescheduleCard({ request, api, onChanged }: CardProps) {
  const [mode, setMode] = useState<'approve' | 'decline' | null>(null);
  const [draft, setDraft] = useState<ApprovalDraft>(() => approvalDraft(request));
  const [declineMessage, setDeclineMessage] = useState('');
  const [conflicts, setConflicts] = useState<ScheduleConflict[] | null>(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = request.status === 'OPEN';

  const edit = (change: Partial<ApprovalDraft>) => {
    setDraft((current) => ({ ...current, ...change }));
    // The conflicts were found for the old date and time
    if ('date' in change || 'startTime' in change) { setConflicts(null); setAck(false); }
  };

  const approve = async (override: boolean) => {
    const problem = approvalProblem(draft);
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    try {
      await api.approveRescheduleRequest(request.id, approvalBody(request, draft, override));
      onChanged(`Approved: the visit moves to ${dateTimeLabel(draft.date, draft.startTime || null)}. The customer is emailed.`);
    } catch (cause) {
      const found = conflictsOf(cause);
      if (found.length > 0) {
        setConflicts(found);
        setAck(false);
        setError(null);
      } else {
        setError(requestActionError(cause, 'Could not approve the request'));
        if (isStaleRequestError(cause)) onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    const problem = requiredTextProblem(declineMessage, 'a message for the customer');
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    try {
      await api.declineRescheduleRequest(request.id, declineMessage.trim());
      onChanged('Request declined. The customer is emailed your message.');
    } catch (cause) {
      setError(requestActionError(cause, 'Could not decline the request'));
      if (isStaleRequestError(cause)) onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg bg-white p-4 shadow" data-testid="reschedule-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words font-semibold text-gray-900">
            <Link to={`/customers/${request.customer.id}`} className="text-blue-700 hover:underline">{request.customer.name}</Link>
          </p>
          <p className="break-words text-sm text-gray-600">{addressLine(request.property)}</p>
        </div>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(request.status)}`}>{RESCHEDULE_OFFICE_LABELS[request.status]}</span>
      </div>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-gray-500">Now</dt><dd>{dateTimeLabel(request.originalDate, request.originalStartTime)}</dd>
        <dt className="text-gray-500">Asked for</dt>
        <dd>
          <strong>{dateTimeLabel(request.requestedDate, request.requestedStartTime)}</strong>
          {request.requestedWindow && <span className="text-gray-600"> ({request.requestedWindow})</span>}
        </dd>
        {request.reason && <><dt className="text-gray-500">Reason</dt><dd className="break-words">{request.reason}</dd></>}
        <dt className="text-gray-500">Asked on</dt><dd>{whenLabel(request.createdAt)}</dd>
        <dt className="text-gray-500">Job</dt>
        <dd><Link to={`/jobs/${request.jobId}`} className="text-blue-700 hover:underline">Open the job</Link> <span className="text-gray-500">({request.jobStatus.replace(/_/g, ' ').toLowerCase()})</span></dd>
        {!open && request.status !== 'CANCELLED' && (request.approvedDate || request.declineReason) && (
          <>
            <dt className="text-gray-500">Outcome</dt>
            <dd className="break-words">
              {request.declineReason ? `Declined: ${request.declineReason}` : `Moved to ${dateTimeLabel(request.approvedDate, request.approvedStartTime)}`}
            </dd>
          </>
        )}
      </dl>

      {open && mode === null && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button type="button" className={primaryButtonClass} onClick={() => { setMode('approve'); setError(null); }}>Approve</button>
          <button type="button" className={secondaryButtonClass} onClick={() => { setMode('decline'); setError(null); }}>Decline</button>
        </div>
      )}

      {open && mode === 'approve' && (
        <form
          className="mt-4 space-y-3 rounded-md bg-gray-50 p-3" aria-label="Approve reschedule request"
          onSubmit={(e) => { e.preventDefault(); void approve(false); }}
        >
          <p className="text-sm text-gray-700">Apply this date and time to the job. Change them if you need to; the customer is told the final date.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={`ap-date-${request.id}`} className={labelClass}>Date</label>
              <input id={`ap-date-${request.id}`} type="date" className={`${inputClass} min-h-[44px]`} value={draft.date} disabled={busy} onChange={(e) => edit({ date: e.target.value })} />
            </div>
            <div>
              <label htmlFor={`ap-time-${request.id}`} className={labelClass}>Start time (blank = none)</label>
              <input id={`ap-time-${request.id}`} type="time" className={`${inputClass} min-h-[44px]`} value={draft.startTime} disabled={busy} onChange={(e) => edit({ startTime: e.target.value })} />
            </div>
          </div>
          <div>
            <label htmlFor={`ap-note-${request.id}`} className={labelClass}>Note for the customer (optional)</label>
            <textarea id={`ap-note-${request.id}`} className={inputClass} rows={2} maxLength={500} value={draft.message} disabled={busy} onChange={(e) => edit({ message: e.target.value })} />
          </div>

          {conflicts && (
            <div role="alert" className="rounded-md bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="reschedule-conflicts">
              <p className="font-semibold">The technician's schedule has conflicts at this time:</p>
              <ul className="mt-1 list-disc pl-5">
                {conflicts.map((c, i) => <li key={`${c.type}-${i}`}>{c.message}</li>)}
              </ul>
              <label className="mt-3 flex min-h-[44px] items-start gap-2">
                <input type="checkbox" className="mt-1 h-5 w-5" checked={ack} disabled={busy} onChange={(e) => setAck(e.target.checked)} />
                <span>I understand and want to apply this time anyway (the conflict is recorded in the audit log).</span>
              </label>
            </div>
          )}
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

          <div className="flex flex-col gap-2 sm:flex-row">
            {conflicts ? (
              <button type="button" className={primaryButtonClass} disabled={busy || !ack} onClick={() => void approve(true)}>Approve anyway</button>
            ) : (
              <button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Approving…' : 'Approve and move the job'}</button>
            )}
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setMode(null); setConflicts(null); setAck(false); setError(null); }}>Cancel</button>
          </div>
        </form>
      )}

      {open && mode === 'decline' && (
        <form
          className="mt-4 space-y-3 rounded-md bg-gray-50 p-3" aria-label="Decline reschedule request"
          onSubmit={(e) => { e.preventDefault(); void decline(); }}
        >
          <div>
            <label htmlFor={`dc-msg-${request.id}`} className={labelClass}>Message for the customer (required)</label>
            <textarea id={`dc-msg-${request.id}`} className={inputClass} rows={3} maxLength={500} value={declineMessage} disabled={busy} onChange={(e) => setDeclineMessage(e.target.value)} />
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Declining…' : 'Decline request'}</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setMode(null); setError(null); }}>Cancel</button>
          </div>
        </form>
      )}
    </li>
  );
}

export function RescheduleRequestsTab() {
  const api = useMemo<IRequestsApi>(() => createAPIClient(), []);
  const [status, setStatus] = useState<RescheduleRequestStatus | ''>('OPEN');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  const fetchPage = useCallback(
    (pageNo: number) => api.listRescheduleRequests({ ...(status ? { status } : {}), page: pageNo, limit: INBOX_PAGE_SIZE }),
    [api, status],
  );
  const { rows, pagination, loading, error, reload } = useInboxList(fetchPage, page, 'Could not load reschedule requests');

  const changed = (message?: string) => {
    setNotice(message ?? null);
    void reload();
  };

  return (
    <section aria-label="Reschedule requests">
      <div className="mb-4 max-w-xs">
        <label htmlFor="reschedule-status" className={labelClass}>Show</label>
        <select
          id="reschedule-status" className={`${inputClass} min-h-[44px]`} value={status}
          onChange={(e) => { setStatus(e.target.value as RescheduleRequestStatus | ''); setPage(1); setNotice(null); }}
        >
          {RESCHEDULE_FILTERS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {notice && <Toast kind="success">{notice}</Toast>}
      {error && (
        <div>
          <Toast kind="error">{error}</Toast>
          <button type="button" className="mb-3 text-sm font-semibold text-blue-700 hover:underline min-h-[44px]" onClick={() => void reload()}>Try again</button>
        </div>
      )}
      {loading && rows.length === 0 && !error && <p role="status" className="text-gray-600">Loading reschedule requests…</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="rounded-lg bg-white p-6 text-center text-gray-600 shadow" data-testid="empty-reschedule">
          {status === 'OPEN' ? 'No reschedule requests are waiting.' : 'No reschedule requests match this filter.'}
        </p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-3" aria-label="Reschedule requests">
          {rows.map((request) => <RescheduleCard key={request.id} request={request} api={api} onChanged={changed} />)}
        </ul>
      )}
      {pagination && <RequestPager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} disabled={loading} />}
    </section>
  );
}
